/**
 * printEmptyPolicy.ts
 * When data is missing (unmapped field or blank Excel cell), print blank
 * instead of placeholders / fallback labels.
 */
import type { FieldFormattingConfig } from "@/utils/formatter";

export interface FillFormatField {
  fieldName: string;
  /** True when mapped to a real Excel column. */
  mapped: boolean;
}

/**
 * Build formatByField for fill/preview/export.
 *
 * - printEmptyWhenMissing=true  → force fallback "" for every field (blank on
 *   missing data); never inject {{placeholder}} ghosts.
 * - printEmptyWhenMissing=false + showUnmappedPlaceholders=true (preview) →
 *   unmapped fields keep `{{tên}}` so the user can see what is unbound.
 * - export path uses showUnmappedPlaceholders=false (unbound stay empty).
 */
export function buildFillFormats(
  base: Record<string, FieldFormattingConfig> | undefined,
  fields: FillFormatField[],
  opts: {
    printEmptyWhenMissing: boolean;
    showUnmappedPlaceholders?: boolean;
  }
): Record<string, FieldFormattingConfig> {
  const next: Record<string, FieldFormattingConfig> = { ...(base ?? {}) };

  if (opts.printEmptyWhenMissing) {
    for (const f of fields) {
      const prev = next[f.fieldName];
      next[f.fieldName] = { ...prev, fallback: "" };
    }
    return next;
  }

  if (opts.showUnmappedPlaceholders) {
    for (const f of fields) {
      if (f.mapped) continue;
      const prev = next[f.fieldName];
      next[f.fieldName] = { ...prev, fallback: `{{${f.fieldName}}}` };
    }
  }

  return next;
}
